import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigate,
  useRoutes,
} from 'react-router';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import { Breadcrumb } from '@britishcouncil/react-admin';
import { useEffect, useRef } from 'react';
import {
  ChooseCentreToUseEventPlannerPlaceholder,
  ChooseRequiredOrganisationContextPlaceholder,
} from 'components';
import { OrganisationStructureLevel, ROUTING_PATHS } from 'const';
import {
  FlexiblePlanningEnabledFilter,
  Layout,
  OrganisationStructureContextFilter,
  useRouteOrganisationContextFilter,
} from 'core';
import { APP_ROUTES, ErrorPage, OidcCallback } from 'pages';
import { useNavigationContext } from 'hooks';
import { useTypedDispatch } from 'store';

function App() {
  const filters = useRouteOrganisationContextFilter(APP_ROUTES);

  const router = createBrowserRouter([
    { path: ROUTING_PATHS.loginCallback, element: <OidcCallback /> },
    {
      path: '*',
      element: (
        <Layout>
          <NavigationContextRedirect />
          <OrganisationStructureContextFilter
            fallbackComponent={<ChooseCentreToUseEventPlannerPlaceholder />}
            filters={[{ required: OrganisationStructureLevel.Centre }]}
          >
            <OrganisationStructureContextFilter
              filters={filters}
              fallbackComponent={<ChooseRequiredOrganisationContextPlaceholder />}
              data-testid="full-page-content"
            >
              <Breadcrumb routes={APP_ROUTES} />
              <FlexiblePlanningEnabledFilter>
                <S.PageContent>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </S.PageContent>
              </FlexiblePlanningEnabledFilter>
            </OrganisationStructureContextFilter>
          </OrganisationStructureContextFilter>
        </Layout>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
}

const AppRoutes = () => {
  const routes = useRoutes(APP_ROUTES);
  return routes;
};

const NavigationContextRedirect = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { organisationId, countryId, centreId, locationId } = useNavigationContext();
  const previousContextRef = useRef({
    organisationId,
    countryId,
    centreId,
    locationId,
  });

  useEffect(() => {
    const previousContext = previousContextRef.current;
    const currentContext = {
      organisationId,
      countryId,
      centreId,
      locationId,
    };

    const hasOrganisationStructureChanged =
      previousContext.organisationId !== currentContext.organisationId ||
      previousContext.countryId !== currentContext.countryId ||
      previousContext.centreId !== currentContext.centreId ||
      previousContext.locationId !== currentContext.locationId;

    if (hasOrganisationStructureChanged && location.pathname !== ROUTING_PATHS.dashboard) {
      navigate(ROUTING_PATHS.dashboard, { replace: true });
    }

    previousContextRef.current = currentContext;
  }, [organisationId, countryId, centreId, locationId, location.pathname, navigate, dispatch]);

  return null;
};

const ErrorBoundary = ({ children }: { children: React.ReactElement }) => {
  const location = useLocation();
  const navigationContext = useNavigationContext();
  const key = `${location.key}-${navigationContext.organisationId}-${navigationContext.countryId}-${navigationContext.centreId}-${navigationContext.locationId}`;

  return (
    <Sentry.ErrorBoundary key={key} fallback={ErrorPage}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default App;

const S = {
  PageContent: styled.div`
    overflow-y: auto;
  `,
};
